/**
 * keyboard_inset_controller.js — move the fixed bottom chrome out of the way
 * while the on-screen keyboard is up (Stimulus).
 *
 * iOS Safari shrinks only the VISUAL viewport when the keyboard opens; the
 * layout viewport is untouched, so a position:fixed bar keeps its place in a
 * layout that is now taller than the visible area and WebKit drags it up with
 * the shrinking viewport. The bottom nav ends up floating above the keys in
 * the middle of the screen.
 *
 * Why focus rather than a viewport measurement: the obvious detector is
 * `window.innerHeight - visualViewport.height > SOME_PX`, but that constant has
 * to clear Safari's ~100px toolbar collapse, and when it guesses wrong it hides
 * the app's primary navigation. Focus on a text-entry element carries the same
 * information with no constant to get wrong — the keyboard is up precisely when
 * something that takes typing has focus. The three controllers already on
 * visualViewport (list_search, ai_chat, public_chat) only ever respond with
 * idempotent scrolls, so none of them had to answer "is the keyboard open"; a
 * show/hide toggle is the first thing here that can be wrong rather than merely
 * redundant, which is what rules the threshold out.
 *
 * This controller sets one attribute on its own element (<body>). CSS owns what
 * moves: mobile-nav.css for the bar itself, ai_chat.css for the Chip launcher,
 * which is positioned off the bar's height and would otherwise be left floating
 * in the gap the bar vacated.
 *
 * Turbo replaces <body> on navigation, so the attribute resets by itself and
 * these listeners go with the old element; disconnect() still removes them
 * explicitly per the controller contract.
 */

import { Controller } from "@hotwired/stimulus";

// The bar is .d-lg-none — above this width there is nothing to move.
const MOBILE = '(max-width: 991.98px)';

// <input> types that raise no keyboard. Everything else counts, including the
// date/time pickers: they cover the bottom of the screen just like a keyboard.
const NO_KEYBOARD = new Set([
    'button', 'checkbox', 'color', 'file', 'hidden',
    'image', 'radio', 'range', 'reset', 'submit',
]);

export default class extends Controller {
    connect() {
        this._onFocusIn = (event) => {
            if (this._takesTyping(event.target)) this._setOpen(true);
        };

        // focusout fires BEFORE the focusin of whatever gains focus next, so
        // moving between two fields would flicker the bar. Settle on the next
        // frame and read where focus actually landed.
        this._onFocusOut = () => {
            cancelAnimationFrame(this._settle);
            this._settle = requestAnimationFrame(() => {
                if (!this._takesTyping(document.activeElement)) this._setOpen(false);
            });
        };

        this.element.addEventListener('focusin', this._onFocusIn);
        this.element.addEventListener('focusout', this._onFocusOut);
    }

    disconnect() {
        this.element.removeEventListener('focusin', this._onFocusIn);
        this.element.removeEventListener('focusout', this._onFocusOut);
        cancelAnimationFrame(this._settle);
        this._onFocusIn = null;
        this._onFocusOut = null;
    }

    _takesTyping(element) {
        if (!element || element === this.element) return false;
        if (element.isContentEditable) return true;
        if (element.tagName === 'TEXTAREA') return true;
        if (element.tagName !== 'INPUT') return false;
        return !NO_KEYBOARD.has((element.type || 'text').toLowerCase());
    }

    // Only gate opening on the breakpoint: closing must always be able to clear
    // the attribute, including after a rotate or resize past the breakpoint.
    _setOpen(open) {
        if (open && !window.matchMedia(MOBILE).matches) return;
        this.element.toggleAttribute('data-keyboard-open', open);
    }
}
